import React, { useState } from 'react';

interface Props {
  replicaId: string;
  replicaName?: string;
  onStartObservation?: (type: string, apps: string[]) => void;
  onStopObservation?: () => void;
}

const OBSERVATION_TYPES = [
  {
    id: 'browser',
    icon: '🌐',
    label: 'Browser Extension',
    description: 'Observe actions in web apps via browser extension',
    available: true,
  },
  {
    id: 'connector',
    icon: '🔌',
    label: 'API Connector',
    description: 'Observe via connected service APIs (Gmail, Calendar, etc.)',
    available: true,
  },
  {
    id: 'desktop',
    icon: '🖥️',
    label: 'Desktop Agent',
    description: 'Native desktop observation with ghost cursor replay',
    available: false,
    comingSoon: true,
  },
];

const APP_PERMISSIONS = [
  { id: 'gmail', icon: '📧', label: 'Gmail', connected: true },
  { id: 'calendar', icon: '📅', label: 'Calendar', connected: true },
  { id: 'drive', icon: '📁', label: 'Google Drive', connected: false },
  { id: 'slack', icon: '💬', label: 'Slack', connected: true },
  { id: 'notion', icon: '📝', label: 'Notion', connected: false },
  { id: 'erp', icon: '📊', label: 'ERP System', connected: false },
];

export const ObservationControls: React.FC<Props> = ({
  replicaName = 'Replica',
  onStartObservation,
  onStopObservation,
}) => {
  const [isObserving, setIsObserving] = useState(false);
  const [selectedType, setSelectedType] = useState<string>('browser');
  const [enabledApps, setEnabledApps] = useState<Set<string>>(new Set(['gmail', 'calendar', 'slack']));
  const [showPrivacyFilter, setShowPrivacyFilter] = useState(true);
  const [duration, setDuration] = useState(0);

  // Timer
  React.useEffect(() => {
    if (!isObserving) return;
    const timer = setInterval(() => setDuration(d => d + 1), 1000);
    return () => clearInterval(timer);
  }, [isObserving]);

  const formatDuration = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  const toggleApp = (id: string) => {
    const next = new Set(enabledApps);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setEnabledApps(next);
  };

  const handleStart = () => {
    setIsObserving(true);
    setDuration(0);
    onStartObservation?.(selectedType, Array.from(enabledApps));
  };

  const handleStop = () => {
    setIsObserving(false);
    onStopObservation?.();
  };

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700/30">
        <div className="flex items-center gap-2">
          <span className="text-lg">👁️</span>
          <h3 className="text-sm font-bold text-white">Observation Mode</h3>
        </div>
        {isObserving && (
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-xs font-mono text-red-400">{formatDuration(duration)}</span>
          </div>
        )}
      </div>

      {/* Active Observation */}
      {isObserving ? (
        <div className="p-5">
          <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-white">Recording in Progress</h4>
                <p className="text-xs text-slate-400">
                  {replicaName} is observing your workflow via {selectedType}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <span>Apps: {Array.from(enabledApps).join(', ')}</span>
              <span>•</span>
              <span className="text-green-400">
                {showPrivacyFilter ? '🔒 Privacy filter ON' : '⚠️ Privacy filter OFF'}
              </span>
            </div>
          </div>

          <button onClick={handleStop}
            className="w-full py-3 bg-red-600 hover:bg-red-500 text-white text-sm font-bold rounded-xl transition-colors flex items-center justify-center gap-2">
            ⏹️ Stop Observation ({formatDuration(duration)})
          </button>
        </div>
      ) : (
        <div className="p-5 space-y-4">
          {/* Observation Type */}
          <div>
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Observation Type</h4>
            <div className="space-y-1.5">
              {OBSERVATION_TYPES.map(type => (
                <button
                  key={type.id}
                  onClick={() => type.available && setSelectedType(type.id)}
                  disabled={!type.available}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                    selectedType === type.id
                      ? 'bg-indigo-600/15 border-indigo-500/30'
                      : type.available
                        ? 'bg-slate-700/10 border-slate-700/30 hover:bg-slate-700/20'
                        : 'bg-slate-800/30 border-slate-700/20 opacity-50'
                  }`}
                >
                  <span className="text-xl">{type.icon}</span>
                  <div className="flex-1">
                    <span className="text-xs font-medium text-white">{type.label}</span>
                    <p className="text-xs text-slate-400">{type.description}</p>
                  </div>
                  {type.comingSoon && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-600/30 text-slate-400">Soon</span>
                  )}
                  {selectedType === type.id && (
                    <span className="text-indigo-400 text-xs">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* App Permissions */}
          <div>
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">App Permissions</h4>
            <div className="grid grid-cols-2 gap-1.5">
              {APP_PERMISSIONS.map(app => (
                <button
                  key={app.id}
                  onClick={() => app.connected && toggleApp(app.id)}
                  disabled={!app.connected}
                  className={`flex items-center gap-2 p-2.5 rounded-lg border text-left transition-all text-xs ${
                    enabledApps.has(app.id)
                      ? 'bg-green-500/10 border-green-500/30 text-green-300'
                      : app.connected
                        ? 'bg-slate-700/10 border-slate-700/30 text-slate-400 hover:text-white'
                        : 'bg-slate-800/20 border-slate-700/20 text-slate-500 opacity-50'
                  }`}
                >
                  <span>{app.icon}</span>
                  <span className="font-medium">{app.label}</span>
                  {!app.connected && <span className="text-xs text-slate-600 ml-auto">—</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Privacy Filter */}
          <div className="flex items-center justify-between p-3 bg-slate-700/20 rounded-xl">
            <div className="flex items-center gap-2">
              <span className="text-sm">🔒</span>
              <div>
                <span className="text-xs font-medium text-white">Privacy Filter</span>
                <p className="text-xs text-slate-400">Auto-mask passwords, tokens, banking</p>
              </div>
            </div>
            <button
              onClick={() => setShowPrivacyFilter(!showPrivacyFilter)}
              className={`w-10 h-5 rounded-full transition-colors relative ${
                showPrivacyFilter ? 'bg-green-500' : 'bg-slate-600'
              }`}
            >
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                showPrivacyFilter ? 'left-5' : 'left-0.5'
              }`} />
            </button>
          </div>

          {/* Start Button */}
          <button onClick={handleStart} disabled={enabledApps.size === 0}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white text-sm font-bold rounded-xl transition-colors flex items-center justify-center gap-2">
            👁️ Start Observation
          </button>
        </div>
      )}
    </div>
  );
};

export default ObservationControls;
